import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  acceptChallenge,
  declineChallenge,
  getMyChessRating,
} from '../api/playing';
import { subscribeIncomingChallenges, subscribeChessSeeks, notifyPlayingChallengesChanged } from '../playing/chessSocket';
import { formatTimeControl, formatExpiryCountdown, challengeTtlMs, isPastTtl } from '../pages/playing/playingUi';
import { getActiveRole, getRoles, getUserId } from '../utils/auth';
import Button from './common/Button';
import UserLink from './common/UserLink';

/** Enough time to read match/accept notice before auto-open. */
const AUTO_OPEN_MS = 3200;

function isParentViewer() {
  return String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
}

/**
 * Global toast for chess challenge + seek-match events (STOMP).
 */
export default function PlayingChallengeToast() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(() => getUserId());
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [ttlNow, setTtlNow] = useState(() => Date.now());
  const autoNavRef = useRef(null);
  const parentViewer = isParentViewer();

  const clearAutoNav = () => {
    if (autoNavRef.current != null) {
      window.clearTimeout(autoNavRef.current);
      autoNavRef.current = null;
    }
  };

  useEffect(() => () => clearAutoNav(), []);

  useEffect(() => {
    const iv = setInterval(() => setTtlNow(Date.now()), 15_000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const fromJwt = getUserId();
    if (fromJwt != null) {
      setUserId(fromJwt);
      return undefined;
    }
    // Older tokens may lack userId claim — fall back to ratings endpoint
    let cancelled = false;
    getMyChessRating()
      .then((me) => {
        if (!cancelled && me?.userId != null) setUserId(me.userId);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (userId == null) return undefined;
    const unsubCh = subscribeIncomingChallenges(userId, (c) => {
      if (!c?.id) return;
      const status = (c.status || 'PENDING').toUpperCase();
      if (status === 'PENDING' && c.toUserId !== userId) return;
      clearAutoNav();
      setActionError('');
      setToast({ kind: 'challenge', ...c });
      notifyPlayingChallengesChanged();
      if (status === 'ACCEPTED' && c.gameId) {
        const path = `/playing/game/${c.gameId}`;
        // Already on the waiting board — WS will flip PENDING→ACTIVE; skip toast auto-nav noise
        if (window.location.pathname === path) {
          setToast(null);
          return;
        }
        autoNavRef.current = window.setTimeout(() => {
          autoNavRef.current = null;
          setToast((cur) => (cur?.kind === 'challenge' && cur?.id === c.id ? null : cur));
          if (window.location.pathname !== path) navigate(path);
        }, AUTO_OPEN_MS);
      }
    });
    const unsubSeek = subscribeChessSeeks((s) => {
      if (!s?.gameId) return;
      const status = (s.status || '').toUpperCase();
      if (status !== 'MATCHED') return;
      // Never trust `mine` on the public lobby topic
      if (Number(s.userId) !== Number(userId)) return;
      const path = `/playing/game/${s.gameId}`;
      if (window.location.pathname === path) return;
      clearAutoNav();
      setActionError('');
      setToast(null);
      navigate(path);
    }, undefined, userId);
    return () => {
      clearAutoNav();
      unsubCh();
      unsubSeek();
    };
  }, [userId, navigate]);

  if (!toast) return null;

  if (toast.kind === 'seek') {
    const tc = formatTimeControl(
      toast.timeControlSeconds,
      toast.incrementSeconds || 0,
      toast.daysPerMove,
    );
    return (
      <div className="playing-challenge-toast" role="status" aria-live="polite">
        <div className="playing-challenge-toast-inner">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              {t('playing.seekMatched')}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {tc || '—'}
              {' · '}
              {toast.rated ? t('playing.ratedGame') : t('playing.casualGame')}
            </p>
          </div>
          <Button
            variant="page-primary"
            onClick={() => {
              clearAutoNav();
              setToast(null);
              navigate(`/playing/game/${toast.gameId}`);
            }}
          >
            {t('playing.open')}
          </Button>
        </div>
      </div>
    );
  }

  const tc = formatTimeControl(toast.timeControlSeconds, toast.incrementSeconds || 0, toast.daysPerMove);
  const status = (toast.status || 'PENDING').toUpperCase();
  const pending = status === 'PENDING';
  const expires = pending
    ? formatExpiryCountdown(toast.createdAt, challengeTtlMs(toast), ttlNow)
    : null;
  const expired = pending && isPastTtl(toast.createdAt, challengeTtlMs(toast), ttlNow);

  let title = t('playing.challengeIncoming');
  if (toast.rematchFromGameId) title = t('playing.rematchIncomingToast', { defaultValue: 'Rematch challenge' });
  if (status === 'ACCEPTED') title = t('playing.challengeAccepted');
  if (status === 'DECLINED') title = t('playing.challengeDeclined');
  if (status === 'CANCELLED') title = t('playing.challengeCancelled');
  if (status === 'EXPIRED' || expired) title = t('playing.challengeExpired', { defaultValue: 'Challenge expired' });

  return (
    <div className="playing-challenge-toast" role="status" aria-live="polite">
      <div className="playing-challenge-toast-inner">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            {title}
          </p>
          <p className="mt-0.5 truncate text-sm font-semibold text-slate-900">
            {pending ? (
              <UserLink
                id={toast.fromUserId}
                name={toast.fromName || t('playing.challengePlayer')}
                className="text-slate-900 hover:text-primary-700"
              />
            ) : (
              <UserLink
                id={toast.toUserId || toast.fromUserId}
                name={toast.toName || toast.fromName || t('playing.challengePlayer')}
                className="text-slate-900 hover:text-primary-700"
              />
            )}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {tc || '—'}
            {' · '}
            {toast.rated ? t('playing.ratedGame') : t('playing.casualGame')}
            {expires ? ` · ${t('playing.expiresIn', { when: expires, defaultValue: 'Expires in {{when}}' })}` : ''}
          </p>
          {parentViewer && pending && !expired && (
            <p className="mt-1 text-xs font-medium text-amber-800">
              {t('playing.parentChallengeToastHint', {
                defaultValue: 'Parents don’t play human games — decline this invite, or play vs the computer from the hub.',
              })}
            </p>
          )}
          {actionError && (
            <p className="mt-1 text-xs font-medium text-rose-700" role="status">
              {actionError}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {pending && !expired && (
            <>
              {!parentViewer && (
              <Button
                variant="page-primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setActionError('');
                  try {
                    const g = await acceptChallenge(toast.id);
                    clearAutoNav();
                    setToast(null);
                    notifyPlayingChallengesChanged();
                    navigate(`/playing/game/${g.id}`);
                  } catch {
                    setActionError(t('playing.challengeAcceptFailed'));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t('playing.accept')}
              </Button>
              )}
              <Button
                variant="page-secondary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setActionError('');
                  try {
                    await declineChallenge(toast.id);
                    clearAutoNav();
                    setToast(null);
                    notifyPlayingChallengesChanged();
                  } catch {
                    setActionError(t('playing.challengeDeclineFailed', { defaultValue: 'Could not decline. Try again.' }));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t('playing.decline')}
              </Button>
            </>
          )}
          {pending && expired && (
            <Button
              variant="page-secondary"
              onClick={() => {
                clearAutoNav();
                setToast(null);
                notifyPlayingChallengesChanged();
              }}
            >
              {t('common.dismiss', { defaultValue: 'Dismiss' })}
            </Button>
          )}
          {status === 'ACCEPTED' && toast.gameId && (
            <Button
              variant="page-primary"
              onClick={() => {
                clearAutoNav();
                setToast(null);
                navigate(`/playing/game/${toast.gameId}`);
              }}
            >
              {t('playing.open')}
            </Button>
          )}
          {!pending && (
            <Button
              variant="page-secondary"
              onClick={() => {
                clearAutoNav();
                setToast(null);
              }}
            >
              {t('playing.dismissResult')}
            </Button>
          )}
          <Link
            to="/playing"
            className="btn-page-action-secondary px-3 py-1.5 text-sm"
            onClick={() => {
              clearAutoNav();
              setToast(null);
            }}
          >
            {t('playing.challenges')}
          </Link>
        </div>
      </div>
    </div>
  );
}
